import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { Box, IconButton, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import { forwardRef, memo, useCallback } from 'react';
import { IMaskInput } from 'react-imask';

const NumberInputComponent = memo(
  forwardRef(({ onChange, name, min, max, maximumFractionDigits = 3, ...props }, ref) => {
    return (
      <IMaskInput
        {...props}
        mask={Number}
        radix='.'
        scale={maximumFractionDigits}
        inputRef={ref}
        min={min}
        max={max}
        onAccept={value => onChange({ target: { name, value } })}
        overwrite
      />
    );
  }),
);

NumberInputComponent.propTypes = {
  name: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  maximumFractionDigits: PropTypes.number,
};

const NumberInput = memo(
  forwardRef(
    (
      { onChange, value, InputProps, NumberInputProps, min, max, maximumFractionDigits, disableInputArrows, ...props },
      ref,
    ) => {
      const onIncreaseClick = useCallback(() => {
        const increasedValue = +value + 1;
        if (Number.isNaN(increasedValue)) {
          return;
        }
        if (Number.isSafeInteger(max) && increasedValue > max) {
          return onChange(`${max}`);
        }
        return onChange(`${increasedValue}`);
      }, [max, onChange, value]);
      const onDecreaseClick = useCallback(() => {
        const decreasedValue = +value - 1;
        if (Number.isNaN(decreasedValue)) {
          return;
        }
        if (Number.isSafeInteger(min) && decreasedValue < min) {
          return onChange(`${min}`);
        }
        return onChange(`${decreasedValue}`);
      }, [min, onChange, value]);

      const onKeyDown = useCallback(
        event => {
          if (!['ArrowUp', 'ArrowDown'].includes(event.code)) return;

          event?.preventDefault?.();
          const currentValue = +value;

          if (Number.isNaN(currentValue)) return;

          const newValue = event.code === 'ArrowUp' ? currentValue + 1 : currentValue - 1;

          if (Number.isSafeInteger(max) && newValue > max) {
            return onChange(`${max}`);
          }
          if (Number.isSafeInteger(min) && newValue < min) {
            return onChange(`${min}`);
          }

          onChange(`${newValue}`);
        },
        [max, min, onChange, value],
      );

      const inputComponent = useCallback(
        forwardRef((inputProps, inputRef) => (
          <NumberInputComponent {...inputProps} {...NumberInputProps} ref={inputRef} />
        )),
        [],
      );

      return (
        <TextField
          onChange={onChange}
          value={value == null ? '' : String(value)}
          ref={ref}
          onKeyDown={onKeyDown}
          {...props}
          slotProps={{
            input: {
              inputComponent,
              endAdornment: !disableInputArrows && (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <IconButton
                    tabIndex={-1}
                    aria-label='increase'
                    disableRipple
                    size='small'
                    sx={{ padding: 0 }}
                    onClick={onIncreaseClick}
                  >
                    <ArrowDropUp sx={{ width: '22px', height: '22px', marginBottom: '-6px' }} />
                  </IconButton>
                  <IconButton
                    tabIndex={-1}
                    aria-label='decrease'
                    disableRipple
                    size='small'
                    sx={{ padding: 0 }}
                    onClick={onDecreaseClick}
                  >
                    <ArrowDropDown sx={{ width: '22px', height: '22px', marginTop: '-6px' }} />
                  </IconButton>
                </Box>
              ),
              inputProps: {
                min,
                max,
                maximumFractionDigits,
              },
              ...InputProps,
            },
          }}
        />
      );
    },
  ),
);

NumberInput.propTypes = {
  onChange: PropTypes.func,
  InputProps: PropTypes.object,
  NumberInputProps: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  min: PropTypes.number,
  max: PropTypes.number,
  maximumFractionDigits: PropTypes.number,
  disableInputArrows: PropTypes.bool,
};

export default NumberInput;
